import React from 'react';
import cx from 'classnames';
import * as _ from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { makeStyles } from 'design-system/product/theme';

type ChatGPTButtonProps = {
  onClick?: () => void;
  primary?: boolean;
  secondary?: boolean;
  tertiary?: boolean;
  base?: boolean;
  icon?: IconProp;
  customIcon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  darkMode?: boolean;
  className?: any;
};

const ChatGPTButton: React.FC<
  ChatGPTButtonProps & React.ButtonHTMLAttributes<HTMLButtonElement>
> = props => {
  const classes = useStyles();

  const type = props.secondary
    ? props.darkMode
      ? 'secondaryDark'
      : 'secondary'
    : props.tertiary
    ? 'tertiary'
    : props.base
    ? 'base'
    : props.darkMode
    ? 'primaryDark'
    : 'primary';

  return (
    <>
      <button
        {..._.omit(
          props,
          'className',
          'primary',
          'secondary',
          'tetriary',
          'base',
          'iconPosition',
          'icon',
          'darkMode',
          'customIcon',
        )}
        className={cx(classes.wrapper, classes[type], props.className)}
        onClick={props.onClick}
      >
        {props.children && <span>{props.children}</span>}
        {props.icon && (
          <FontAwesomeIcon
            icon={props.icon}
            className={cx(classes.icon, props.iconPosition === 'left' && classes.iconLeft)}
          />
        )}
        {props.customIcon && (
          <div className={cx(classes.icon, props.iconPosition === 'left' && classes.iconLeft)}>
            {props.customIcon}
          </div>
        )}
      </button>
    </>
  );
};

export default ChatGPTButton;

const useStyles = makeStyles(theme => ({
  wrapper: {
    fontWeight: 400,
    fontSize: theme.typography.fontSize,
    background: 'transparent',
    height: theme.spacing(4),
    border: 1,
    borderRadius: 4,
    borderStyle: 'solid',
    display: 'flex',
    alignItems: 'center',
    gap: `${theme.spacing(1)}px`,
    position: 'relative',
    overflow: 'hidden',
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    '&:not(:disabled)': {
      cursor: 'pointer',
    },
    cursor: 'pointer',
  },
  primary: {
    color: '#374151',
    background: 'white',
    borderColor: 'rgba(0, 0, 0, 0.1)',
    '&:hover': {
      backgroundColor: 'rgb(217, 217, 217)',
    },
    transition: 'background 0.3s',
  },
  primaryDark: {
    color: theme.colors.white,
    background: 'rgba(52, 53, 65)',
    borderColor: 'rgb(86, 88, 105)',
  },
  secondary: {
    color: 'rgb(172, 172, 190)',
    background: 'transparent',
    border: 'none',
    '&:hover': {
      color: 'rgb(55, 65, 81)',
      backgroundColor: 'rgb(217, 217, 217)',
    },
    transition: 'background 0.3s',
    height: 24,
  },
  secondaryDark: {
    color: 'rgb(172, 172, 190)',
    background: 'transparent',
    border: 'none',
    '&:hover': {
      color: 'rgb(217, 217, 227)',
      backgroundColor: 'rgb(68, 70, 83)',
    },
    transition: 'background 0.3s',
    height: 24,
  },
  tertiary: {
    color: theme.palette.primary.main,
    background: theme.colors.white,
    borderColor: theme.colors.periwinkle50,
    '&$active': {
      background: theme.colors.periwinkle5,
    },
  },
  base: {
    color: theme.colors.black,
    background: theme.colors.gray10,
    border: 0,
  },
  icon: {
    width: theme.spacing(2),
    height: theme.spacing(2),
    flexShrink: 0,
  },
  iconLeft: {
    order: 1,
  },
}));
